import { useTranslation } from "@i18n";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getEstimateTotal,
  getEstimateAdditionalCostTotal,
  getEstimateNumberValueBasedOnSettings,
} from "@Actions/estimation";
import { useParams } from "react-router-dom";
import type { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { RootState } from "src/reducers";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type CurrencyData = EstimateCurrency;

type EstimationFooterProps = {
  estimateId?: ObjectIdString;
  currencyData: CurrencyData;
};

function EstimationFooter({ estimateId, currencyData }: EstimationFooterProps) {
  const { t } = useTranslation();
  // Route param is the external entry point for this id; the backend route schema validates it.
  const params = useParams<{ versionId: ObjectIdString }>();
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

  const [estimateTotal, setEstimateTotal] = useState(0);
  const additionalCostTotal = useSelector((state: RootState) => state.additionalCostTotal) as number;
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;

  const fetchEstimateTotal = useCallback(async () => {
    if (!estimateId) {
      return;
    }
    const response = await getEstimateTotal({
      estimateId,
      versionId: params.versionId,
    });

    if (response.data.status && response.data.data !== undefined) {
      setEstimateTotal(response.data.data);
    }
  }, [estimateId, params.versionId]);

  const fetchEstimateAdditionalCostTotal = useCallback(() => {
    if (!estimateId) {
      return;
    }
    dispatch(
      getEstimateAdditionalCostTotal({
        estimateId,
        versionId: params.versionId,
      }),
    );
  }, [estimateId, params.versionId]);

  useEffect(() => {
    fetchEstimateAdditionalCostTotal();
    fetchEstimateTotal();
  }, [fetchEstimateAdditionalCostTotal, fetchEstimateTotal]);

  return (
    <>
      <div className="flex flex-col">
        <div className="p-4 flex flex-row [&_p]:text-[#333] [&_p]:text-sm">
          <div className="flex flex-col w-full px-1 justify-start [&_p]:mb-1">
            <div className="flex items-center justify-between">
              <p>{t("Total")}</p>
              <p>
                {currencyData.category === "Prefix" ? (currencyData.selectedSymbol?.name ?? "") : null}{" "}
                {getEstimateNumberValueBasedOnSettings(estimateTotal, estimateData, "amount")}
                {currencyData.category === "Suffix" ? (currencyData.selectedSymbol?.name ?? "") : null}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <p>{t("Total Amount from additional costs")}</p>
              <p>
                {currencyData.category === "Prefix" ? (currencyData.selectedSymbol?.name ?? "") : null}{" "}
                {getEstimateNumberValueBasedOnSettings(additionalCostTotal, estimateData, "amount")}
                {currencyData.category === "Suffix" ? (currencyData.selectedSymbol?.name ?? "") : null}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p>{t("Grand Total")}</p>
              <p>
                {currencyData.category === "Prefix" ? (currencyData.selectedSymbol?.name ?? "") : null}{" "}
                {getEstimateNumberValueBasedOnSettings(estimateTotal + additionalCostTotal, estimateData, "amount")}
                {currencyData.category === "Suffix" ? (currencyData.selectedSymbol?.name ?? "") : null}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EstimationFooter;
